import { Assets } from 'pixi.js';
import { ASSET_BUNDLES, SPRITESHEET_TO_BUNDLE } from '../data/asset-bundles';
import { AllSpritesheets } from '../data/spritesheets/generated';
import { LocationSpritesheets } from '../data/spritesheets/pois.config';
import { buildSpritesheets } from '../utils/spritesheet-build';
import { useSpritesStore } from '../store/spritesSlice';
import { audioService } from './AudioService';

const ALL = [...AllSpritesheets, ...LocationSpritesheets];
const BY_NAME = new Map(ALL.map((e) => [e.name, e]));

class BundleLoaderService {
  private loaded = new Set<string>();
  private inflight = new Map<string, Promise<void>>();

  public isBundleLoaded(id: string): boolean {
    return this.loaded.has(id);
  }

  public ensureBundle(id: string): Promise<void> {
    if (this.loaded.has(id)) {
      return Promise.resolve();
    }
    const existing = this.inflight.get(id);
    if (existing) {
      return existing;
    }
    const bundle = ASSET_BUNDLES[id];
    if (!bundle) {
      return Promise.resolve();
    }
    const promise = (async () => {
      if (bundle.textures.length > 0) {
        // allSettled, not all: a single missing/undecodable asset (e.g. a
        // placeholder map whose art is not authored yet) must not brick boot.
        const results = await Promise.allSettled(
          bundle.textures.map((url) => Assets.load(url)),
        );
        if (import.meta.env.DEV) {
          results.forEach((r, i) => {
            if (r.status === 'rejected') {
              // eslint-disable-next-line no-console
              console.warn(
                `[bundle:${id}] texture failed: ${bundle.textures[i]}`,
                r.reason,
              );
            }
          });
        }
      }
      const built = useSpritesStore.getState().textures;
      const entries = bundle.spritesheets
        .map((n) => BY_NAME.get(n))
        .filter((e): e is NonNullable<typeof e> => !!e)
        // Skip sheets already built (shared across bundles, e.g. a monster in
        // two biomes) — re-parsing re-registers frames in PIXI's global cache
        // and spams "Texture added to the cache ... already had an entry".
        .filter((e) => !built[e.name]);
      if (entries.length > 0) {
        const sheets = await buildSpritesheets(entries);
        useSpritesStore.getState().addTextures(sheets);
      }
      audioService.prewarm(bundle.music, bundle.sfx);
      this.loaded.add(id);
    })().finally(() => {
      this.inflight.delete(id);
    });
    this.inflight.set(id, promise);
    return promise;
  }

  public prefetchBundle(id: string): void {
    void this.ensureBundle(id).catch(() => {});
  }

  /** Safety net: load the bundle owning `name` if a sprite read missed. */
  public ensureSheetLoaded(name: string): Promise<void> | null {
    if (useSpritesStore.getState().textures[name]) {
      return null;
    }
    const owner = SPRITESHEET_TO_BUNDLE[name];
    return owner ? this.ensureBundle(owner) : null;
  }
}

export const bundleLoaderService = new BundleLoaderService();
